import { SelectOption } from '@globaleris/ui.library'
import {
  cfExecutionModel,
  cfExecutionTableModel
} from '../../api/Argentia/endpoints/invoiceCyclesExecutions/model/cfExecutionModel'
import { InvoiceCycleCompatibilities } from '../../api/Argentia/endpoints/invoiceCyclesExecutions/model/invoiceCycleCompatibilities'
import { EnumSubtipoCiclo } from '../../api/oData/enums/enumSubtipoCiclo'
import { EnumTipoCicloFacturacion } from '../../api/oData/enums/enumTipoCicloFacturacion'
import { EnumTipoFacturacion } from '../../api/oData/enums/enumTipoFacturacion'
import { EnumTipoSujeto } from '../../api/oData/enums/enumTipoSujeto'
import { enumTranslateOption } from '../../helpers/enumHelper'
import { CycleExecutionModel, CycleExecutionWithInvoiceCycleModel } from './invoiceCycles.model'

export const cfExecutionModelListToCycleExecutionModelWithCycleList = (
  cfExecutionList: cfExecutionModel[]
): CycleExecutionWithInvoiceCycleModel[] => {
  return cfExecutionList.map(cfExecutionModelToCycleExecutionWithCycleModel)
}

export const cfExecutionModelToCycleExecutionWithCycleModel = (
  cfExecution: cfExecutionModel
): CycleExecutionWithInvoiceCycleModel => {
  return {
    id: cfExecution.cfExecutionId,
    status: cfExecution.cycleStatus,
    startDate: cfExecution.cfExecutionStartDate,
    endDate: cfExecution.cfExecutionEndDate,
    executionDate: cfExecution.executionDate,
    dateFrom: cfExecution.cfExecutionStartDate,
    dateTo: cfExecution.cfExecutionEndDate,
    invoiceCycleDesc: cfExecution.invoiceCycle.description,
    invoiceCycleId: cfExecution.invoiceCycle.invoiceCycleId,
    invoiceCycleType: cfExecution.invoiceCycle.invoiceType,
    invoiceCycleSubtype: cfExecution.invoiceCycle.invoiceSubType,
    invoiceCycleSubjectType: cfExecution.invoiceCycle.subjectType,
    invoiceCycleBillingType: cfExecution.invoiceCycle.invoiceType,
    subtype: cfExecution.subtype
  }
}

export const cfExecutionModelListToCycleExecutionModelList = (
  cfExecutionList: cfExecutionModel[]
): CycleExecutionModel[] => {
  return cfExecutionList.map(cfExecution => ({
    id: cfExecution.cfExecutionId,
    status: cfExecution.cycleStatus,
    startDate: cfExecution.cfExecutionStartDate,
    endDate: cfExecution.cfExecutionEndDate,
    executionDate: cfExecution.executionDate,
    dateFrom: cfExecution.cfExecutionStartDate,
    dateTo: cfExecution.cfExecutionEndDate,
    invoiceCycleId: cfExecution.invoiceCycle?.invoiceCycleId,
    subtype: cfExecution.subtype
  }))
}

export const cfExecutionTableModelToCycleExecutionModel = (
  cfExecution: cfExecutionModel
): CycleExecutionModel => {
  return {
    id: cfExecution.cfExecutionId,
    status: cfExecution.cycleStatus,
    startDate: cfExecution.cfExecutionStartDate,
    endDate: cfExecution.cfExecutionEndDate,
    executionDate: cfExecution.executionDate,
    dateFrom: cfExecution.cfExecutionStartDate,
    dateTo: cfExecution.cfExecutionEndDate,
    subtype: cfExecution.subtype,
    reInvoiceType: cfExecution.reInvoiceType,
    groupingType: cfExecution.groupingType,
    invoiceCycleId: cfExecution.invoiceCycle?.invoiceCycleId
  }
}

// Map compatibilities data to SelectOption arrays
export const mapCompatibilitiesToSelectOptions = (data: InvoiceCycleCompatibilities[]) => {
  // Extract unique values for each attribute and map to SelectOption arrays
  const subjectTypeIds = [...new Set(data.map(item => item.subjectType))]
  const invoiceTypeIds = [...new Set(data.map(item => item.invoiceType))]
  const cycleIds = [...new Set(data.map(item => item.invoiceCycleType))]
  const subtypeIds = [...new Set(data.map(item => item.invoiceCycleSubtype))]

  const subjectTypes: SelectOption[] = subjectTypeIds.map(id => ({
    value: id,
    label: enumTranslateOption(id, EnumTipoSujeto, 'enums:EnumTipoSujeto')
  }))

  const invoiceTypes: SelectOption[] = invoiceTypeIds.map(id => ({
    value: id,
    label: enumTranslateOption(id, EnumTipoFacturacion, 'enums:EnumTipoFacturacion')
  }))

  const cycles: SelectOption[] = cycleIds
    .filter(id => id !== null)
    .map(id => ({
      value: id!,
      label: enumTranslateOption(id!, EnumTipoCicloFacturacion, 'enums:EnumTipoCicloFacturacion')
    }))

  const subtypes: SelectOption[] = subtypeIds
    .filter(id => id !== null)
    .map(id => ({
      value: id!,
      label: enumTranslateOption(id!, EnumSubtipoCiclo, 'enums:EnumSubtipoCiclo')
    }))

  return {
    subjectTypes,
    invoiceTypes,
    cycles,
    subtypes
  }
}

// Filter options based on compatibility data and current selections
export interface FormSelections {
  subjectType?: number
  invoiceType?: number
  cycle?: number | null
  subtype?: number | null
}

export const getFilteredCompatibilityOptions = (
  allCompatibilities: InvoiceCycleCompatibilities[],
  allOptions: ReturnType<typeof mapCompatibilitiesToSelectOptions>,
  currentSelections: FormSelections
) => {
  // If no selections made, return all options
  if (
    !currentSelections.subjectType &&
    !currentSelections.invoiceType &&
    !currentSelections.cycle &&
    !currentSelections.subtype
  ) {
    return allOptions
  }

  // Filter compatibilities based on current selections
  let filteredCompatibilities = allCompatibilities

  if (currentSelections.subjectType) {
    filteredCompatibilities = filteredCompatibilities.filter(
      item => item.subjectType === currentSelections.subjectType
    )
  }

  if (currentSelections.invoiceType) {
    filteredCompatibilities = filteredCompatibilities.filter(
      item => item.invoiceType === currentSelections.invoiceType
    )
  }

  if (currentSelections.cycle) {
    filteredCompatibilities = filteredCompatibilities.filter(
      item => item.invoiceCycleType === currentSelections.cycle
    )
  }

  if (currentSelections.subtype) {
    filteredCompatibilities = filteredCompatibilities.filter(
      item => item.invoiceCycleSubtype === currentSelections.subtype
    )
  }

  // Extract unique values from filtered compatibilities
  const compatibleSubjectTypes = [...new Set(filteredCompatibilities.map(item => item.subjectType))]
  const compatibleInvoiceTypes = [...new Set(filteredCompatibilities.map(item => item.invoiceType))]
  const compatibleCycles = [...new Set(filteredCompatibilities.map(item => item.invoiceCycleType))]
  const compatibleSubtypes = [
    ...new Set(filteredCompatibilities.map(item => item.invoiceCycleSubtype))
  ]

  // Filter the original options to only include compatible ones
  const filteredSubjectTypes = allOptions.subjectTypes.filter(option =>
    compatibleSubjectTypes.includes(Number(option.value))
  )

  const filteredInvoiceTypes = allOptions.invoiceTypes.filter(option =>
    compatibleInvoiceTypes.includes(Number(option.value))
  )

  const filteredCycles = allOptions.cycles.filter(option =>
    compatibleCycles.includes(Number(option.value))
  )

  const filteredSubtypes = allOptions.subtypes.filter(option =>
    compatibleSubtypes.includes(Number(option.value))
  )

  return {
    subjectTypes: filteredSubjectTypes,
    invoiceTypes: filteredInvoiceTypes,
    cycles: filteredCycles,
    subtypes: filteredSubtypes
  }
}
